import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authGuard } from '@core/guards';
import { canDeactivateGuard } from '@core/guards/can-deactivate.guard';
import { hasMainRoleGuard } from '@core/guards/has-main-role.guard';
import { hasRoleGuard } from '@core/guards/has-role.guard';
import { projectBriefingGuard } from '@core/guards/project-briefing.guard';
import { ProjectInfoService } from '@pages/project/services/project-info.service';
import { APP_ROUTES } from '@shared/common';
import { UserRole } from 'jplus-design-system-angular';

const projectTemplatesChildrenRoutes: Routes = [
  {
    path: '',
    redirectTo: APP_ROUTES.projectTemplateDetails.generalInfo,
    pathMatch: 'full'
  },
  {
    path: APP_ROUTES.projectTemplateDetails.generalInfo,
    canDeactivate: [canDeactivateGuard],
    loadComponent: () => import('./pages/templates/project-template/general-info/general-info.page').then(m => m.GeneralInfoPageComponent)
  },
  {
    path: APP_ROUTES.projectTemplateDetails.briefFields,
    canDeactivate: [canDeactivateGuard],
    loadComponent: () => import('./shared/components/brief-fields/brief-fields.page').then(m => m.BriefFieldsPageComponent)
  },
  {
    path: APP_ROUTES.projectTemplateDetails.taskFields,
    canDeactivate: [canDeactivateGuard],
    loadComponent: () => import('./shared/components/task-fields/task-fields.page').then(m => m.TaskFieldsPageComponent)
  }
];

const projectRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/project/project.page').then(m => m.ProjectPageComponent),
    canActivate: [hasRoleGuard([UserRole.Jellyfish, UserRole.Client])],
    children: [
      {
        path: '',
        redirectTo: APP_ROUTES.briefing,
        pathMatch: 'full'
      },
      {
        path: APP_ROUTES.briefing,
        loadComponent: () => import('./pages/project/briefing/briefing.page').then(m => m.BriefingPageComponent),
        canActivate: [projectBriefingGuard]
      },
      {
        path: APP_ROUTES.inProduction,
        loadComponent: () => import('./pages/project/in-production/in-production.page').then(m => m.InProductionPageComponent),
        canActivate: [hasRoleGuard([UserRole.Jellyfish])]
      },
      {
        path: APP_ROUTES.clientOverview,
        loadComponent: () => import('./pages/project/client-overview/client-overview.component').then(m => m.ClientOverviewComponent)
      },
      {
        path: APP_ROUTES.withClient,
        loadComponent: () => import('./pages/project/with-client/with-client.page').then(m => m.WithClientPageComponent)
      },
      {
        path: APP_ROUTES.completed,
        loadComponent: () => import('./pages/project/completed/completed.page').then(m => m.CompletedPageComponent)
      }
    ]
  },
  {
    path: APP_ROUTES.info,
    loadComponent: () => import('./pages/project/project-info/project-info.page').then(m => m.ProjectInfoPageComponent),
    canActivate: [hasRoleGuard([UserRole.Jellyfish])],
    canDeactivate: [canDeactivateGuard]
  },
  {
    path: APP_ROUTES.template,
    loadComponent: () => import('./pages/project/template/template.page').then(m => m.TemplatePageComponent),
    canActivate: [hasRoleGuard([UserRole.Jellyfish])],
    children: [
      {
        path: '',
        redirectTo: APP_ROUTES.projectTemplateDetails.briefFields,
        pathMatch: 'full'
      },
      {
        path: APP_ROUTES.projectTemplateDetails.briefFields,
        loadComponent: () => import('./shared/components/brief-fields/brief-fields.page').then(m => m.BriefFieldsPageComponent),
        canDeactivate: [canDeactivateGuard]
      },
      {
        path: APP_ROUTES.projectTemplateDetails.taskFields,
        loadComponent: () => import('./shared/components/task-fields/task-fields.page').then(m => m.TaskFieldsPageComponent),
        canDeactivate: [canDeactivateGuard]
      }
    ]
  },
  {
    path: APP_ROUTES.team,
    loadComponent: () => import('./pages/project/team/team.page').then(m => m.TeamPageComponent),
    canActivate: [hasRoleGuard([UserRole.Jellyfish])],
    canDeactivate: [canDeactivateGuard]
  },
  {
    path: APP_ROUTES.userAllocation,
    loadComponent: () => import('./pages/project/user-allocation/user-allocation.page').then(m => m.UserAllocationPageComponent),
    canActivate: [hasRoleGuard([UserRole.Jellyfish])]
  },
  {
    path: ':batchId',
    children: [
      {
        path: ':stageId',
        pathMatch: 'full',
        loadComponent: () => import('./pages/project/deliverables-list/deliverables-list.page').then(m => m.DeliverablesListPageComponent),
        canActivate: [hasRoleGuard([UserRole.Jellyfish, UserRole.Client])]
      },
      {
        path: `${APP_ROUTES.deliverable}/:deliverableId`,
        loadComponent: () => import('./pages/project/deliverable/deliverable.page').then(m => m.DeliverablePageComponent),
        canDeactivate: [canDeactivateGuard]
      }
    ]
  }
];

const routes: Routes = [
  { path: '', redirectTo: APP_ROUTES.redirect, pathMatch: 'full' },
  {
    path: APP_ROUTES.authCallback,
    loadComponent: () => import('./pages/auth-callback/auth-callback.component').then(m => m.AuthCallbackComponent)
  },
  {
    path: APP_ROUTES.login,
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: APP_ROUTES.accessDenied,
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    data: {
      accessDenied: true
    }
  },
  {
    path: '',
    loadComponent: () => import('./pages/main/main.component').then(m => m.MainComponent),
    canActivate: [authGuard],
    children: [
      {
        path: APP_ROUTES.redirect,
        loadComponent: () => import('./pages/home-redirect/home-redirect.component').then(m => m.HomeRedirectComponent)
      },
      {
        path: APP_ROUTES.clients,
        loadComponent: () => import('./pages/clients/clients.component').then(m => m.ClientsComponent),
        canActivate: [hasMainRoleGuard]
      },
      {
        path: APP_ROUTES.reportingChats,
        loadComponent: () => import('./pages/reporting-chats/reporting-chats.component').then(m => m.ReportingChatsComponent)
      },
      {
        path: `:${APP_ROUTES.client}`,
        canActivate: [hasRoleGuard(Object.values(UserRole))],
        children: [
          {
            path: APP_ROUTES.batches,
            loadChildren: () => import('./pages/batches/batches.module').then(m => m.BatchesModule)
          },
          {
            path: APP_ROUTES.jobs,
            loadComponent: () => import('./pages/jobs/jobs.page').then(m => m.LatestJobsPageComponent),
            canActivate: [hasRoleGuard([UserRole.Jellyfish])]
          },
          {
            path: APP_ROUTES.templates,
            loadComponent: () => import('./pages/templates/templates.page').then(m => m.TemplatesPageComponent),
            canActivate: [hasRoleGuard([UserRole.Jellyfish])],
            children: [
              {
                path: '',
                redirectTo: APP_ROUTES.list,
                pathMatch: 'full'
              },
              {
                path: APP_ROUTES.list,
                loadComponent: () => import('@pages/templates/list/list.page').then(m => m.ListPageComponent),
                children: [
                  {
                    path: '',
                    redirectTo: APP_ROUTES.projectTemplates,
                    pathMatch: 'full'
                  },
                  {
                    path: APP_ROUTES.projectTemplates,
                    loadComponent: () =>
                      import('@pages/templates/list/project-templates/project-templates.page').then(m => m.ProjectTemplatesPageComponent)
                  },
                  {
                    path: APP_ROUTES.promptTemplates,
                    loadComponent: () =>
                      import('@pages/templates/list/prompt-templates/prompt-templates.page').then(m => m.PromptTemplatesPageComponent)
                  }
                ]
              },
              {
                path: APP_ROUTES.createProject,
                loadComponent: () =>
                  import('@pages/templates/project-template/project-template.page').then(m => m.ProjectTemplatePageComponent),
                children: projectTemplatesChildrenRoutes
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('@pages/templates/project-template/project-template.page').then(m => m.ProjectTemplatePageComponent),
                children: projectTemplatesChildrenRoutes
              }
            ]
          },
          {
            path: APP_ROUTES.projects,
            loadComponent: () => import('./pages/projects/projects.page').then(m => m.ProjectsPageComponent)
          },
          {
            path: APP_ROUTES.project,
            children: projectRoutes,
            providers: [ProjectInfoService]
          },
          {
            path: APP_ROUTES.people,
            loadComponent: () => import('./pages/people/people.page').then(m => m.PeoplePageComponent),
            canActivate: [hasRoleGuard([UserRole.Jellyfish])]
          },
          {
            path: APP_ROUTES.settings,
            loadComponent: () => import('./pages/client-settings/client-settings.component').then(m => m.ClientSettingsComponent),
            canActivate: [hasRoleGuard([UserRole.Jellyfish])]
          },
          {
            path: APP_ROUTES.tasks,
            loadComponent: () => import('./pages/tasks/tasks.component').then(m => m.TasksComponent),
            children: [
              {
                path: '',
                redirectTo: 'in-progress',
                pathMatch: 'full'
              },
              {
                path: 'in-progress',
                loadComponent: () => import('./pages/tasks/in-progress/in-progress.component').then(m => m.InProgressComponent)
              },
              {
                path: 'approved',
                loadComponent: () => import('./pages/tasks/approved/approved.component').then(m => m.ApprovedComponent)
              }
            ]
          },
          {
            path: `${APP_ROUTES.tasks}/:batchId/stages/:stageId/deliverables`,
            loadComponent: () => import('./pages/tasks/deliverable-list/deliverable-list.component').then(m => m.DeliverableListComponent),
            canActivate: [hasRoleGuard([UserRole.Freelance])]
          },
          { path: '', redirectTo: APP_ROUTES.projects, pathMatch: 'full' }
        ]
      }
    ]
  },
  { path: '**', redirectTo: APP_ROUTES.redirect }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
